<script setup lang="ts">
import CostChart from '../components/CostChart.vue'

const RULES = [
    {
        name: 'The $5,000 rule',
        formula: 'age × repair cost > $5,000 → lean replace',
        example: 'A 14-year-old system with a $500 repair: 14 × $500 = $7,000. Even a modest repair on an old system fails the math.',
    },
    {
        name: 'The 50% rule',
        formula: 'repair quote > 50% of replacement → lean replace',
        example: 'A $5,000 compressor job against a $9,000 replacement is buying half a new system — without getting one.',
    },
    {
        name: 'The refrigerant reality',
        formula: 'R-22 system + refrigerant work → almost always replace',
        example: 'R-22 production ended in 2020. Recharges draw on recycled supply at $150–$300+ per pound — repairs that used to be $300 now run four figures.',
    },
]
</script>

<template>
    <section id="math" class="site-section">
        <div class="site-content">
            <span class="kicker" v-reveal>Show your work</span>
            <h2 v-reveal>The math we use</h2>
            <p class="muted intro">
                The verdict isn't a vibe — it's three published rules the trade has used for
                years, plus your system's age, history, and comfort record. Here they are, in
                full.
            </p>

            <div class="rules">
                <div v-for="(rule, i) in RULES" :key="rule.name" v-reveal="i * 90" class="rule">
                    <span class="index">{{ String(i + 1).padStart(2, '0') }}</span>
                    <h3>{{ rule.name }}</h3>
                    <code>{{ rule.formula }}</code>
                    <p class="muted">{{ rule.example }}</p>
                </div>
            </div>

            <div class="chart-wrap" v-reveal>
                <h3>What things actually cost</h3>
                <p class="muted">
                    Typical 2026 US ranges — real quotes vary by region, brand, and house.
                    Anything cost-related on this site is a range, never a promise.
                </p>
                <CostChart />
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.intro {
    max-width: 42rem;
}

.rules {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: var(--site-space-md);
    margin: var(--site-space-lg) 0 var(--site-space-xl);
}

.rule {
    position: relative;
    overflow: hidden;
    border: 2px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-lg);
    background: var(--site-surface);

    .index {
        position: absolute;
        top: -0.35em;
        right: 0.1em;
        font-family: var(--site-font-heading);
        font-size: 4.6rem;
        line-height: 1;
        color: var(--site-accent-soft);
        user-select: none;
    }

    h3 {
        position: relative;
        font-size: 1.05rem;
        margin-bottom: var(--site-space-sm);
    }

    code {
        display: block;
        font-family: var(--site-font-mono);
        font-size: 0.85rem;
        color: var(--site-accent-strong);
        background: var(--site-accent-soft);
        padding: 0.5rem 0.7rem;
        border-radius: var(--site-radius-sm);
        margin-bottom: var(--site-space-sm);
    }

    p {
        font-size: 0.92rem;
        margin: 0;
    }
}

.chart-wrap {
    border: 2px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-lg);
    background: var(--site-surface);

    p {
        font-size: 0.92rem;
    }
}
</style>
